import type { Core } from '@pdftron/webviewer';
import { getIsAlineAnnotation } from 'src/components/viewer/helpers/is-aline-annotation';
import { pdfTronCore } from 'src/components/viewer/viewer-context';
import { Sentry } from 'src/sentry';

type PdfDoc = Awaited<ReturnType<Core.Document['getPDFDoc']>>;
type LoadedDocument = Pick<Core.Document, 'getPDFDoc'>;

interface FlattenedSignatureWidgets {
  flattenedFieldNames: Set<string>;
  pagesToRefresh: Set<number>;
}

/** Minimal viewer surface needed to bake external digsig appearances into page content. */
export interface SignatureAppearanceFlattenViewer {
  getAnnotationManager(): Pick<Core.AnnotationManager, 'drawAnnotations' | 'getAnnotationsList'>;
  getDocument(): LoadedDocument | null;
  refreshPage(pageNumber: number): void;
  updateView(): void;
}

const flattenedSignatureWidgets = new WeakSet<Core.Annotations.WidgetAnnotation>();

/**
 * Bake external digital-signature widget appearances into page content so they
 * show in WebViewer. Skips Aline fields so our signing widgets stay interactive.
 */
export async function flattenExternalSignedSignatureAppearances(
  documentViewer: SignatureAppearanceFlattenViewer
): Promise<void> {
  try {
    const loadedDocument = documentViewer.getDocument();
    if (!loadedDocument) return;

    const annotationManager = documentViewer.getAnnotationManager();
    const pdfDoc = await loadedDocument.getPDFDoc();
    if (!isCurrentDocument(documentViewer, loadedDocument)) return;

    const widgetsToFlatten = await collectExternalSignedSignatureWidgets(annotationManager, pdfDoc);
    if (widgetsToFlatten.length === 0) return;
    if (!isCurrentDocument(documentViewer, loadedDocument)) return;

    const fieldNamesToFlatten = new Set(widgetsToFlatten.map(widget => widget.fieldName));
    const pageNumbers = [...new Set(widgetsToFlatten.map(widget => widget.PageNumber))];
    const { flattenedFieldNames, pagesToRefresh } = await flattenPdfNetSignatureWidgets(
      pdfDoc,
      fieldNamesToFlatten,
      pageNumbers
    );
    if (pagesToRefresh.size === 0) return;
    if (!isCurrentDocument(documentViewer, loadedDocument)) return;

    for (const widget of widgetsToFlatten)
      if (flattenedFieldNames.has(widget.fieldName)) flattenedSignatureWidgets.add(widget);

    for (const pageNumber of pagesToRefresh) {
      if (!isCurrentDocument(documentViewer, loadedDocument)) return;
      documentViewer.refreshPage(pageNumber);
      await annotationManager.drawAnnotations({ majorRedraw: true, pageNumber });
      hideFlattenedSignatureWidgetElements(annotationManager, pageNumber);
    }
    if (!isCurrentDocument(documentViewer, loadedDocument)) return;
    documentViewer.updateView();
  } catch (error) {
    Sentry.captureException(error);
  }
}

export function hideFlattenedSignatureWidgetElements(
  annotationManager: Pick<Core.AnnotationManager, 'getAnnotationsList'>,
  pageNumber: number
): void {
  for (const annotation of annotationManager.getAnnotationsList()) {
    if (
      annotation.PageNumber === pageNumber &&
      annotation instanceof pdfTronCore.Annotations.WidgetAnnotation &&
      flattenedSignatureWidgets.has(annotation) &&
      annotation.element
    )
      annotation.element.style.display = 'none';
  }
}

function isCurrentDocument(
  documentViewer: SignatureAppearanceFlattenViewer,
  loadedDocument: LoadedDocument
): boolean {
  return documentViewer.getDocument() === loadedDocument;
}

async function collectExternalSignedSignatureWidgets(
  annotationManager: Pick<Core.AnnotationManager, 'getAnnotationsList'>,
  pdfDoc: PdfDoc
): Promise<Core.Annotations.SignatureWidgetAnnotation[]> {
  const widgets: Core.Annotations.SignatureWidgetAnnotation[] = [];

  for (const annot of annotationManager.getAnnotationsList()) {
    if (!(annot instanceof pdfTronCore.Annotations.SignatureWidgetAnnotation)) continue;
    if (getIsAlineAnnotation(annot)) continue;
    if (await shouldFlattenExternalSignatureWidget(pdfDoc, annot)) widgets.push(annot);
  }

  return widgets;
}

async function shouldFlattenExternalSignatureWidget(
  pdfDoc: PdfDoc,
  widget: Core.Annotations.SignatureWidgetAnnotation
): Promise<boolean> {
  const field = await pdfDoc.getField(widget.fieldName);
  if (!(await field.isValid())) return false;

  const digSig = await pdfTronCore.PDFNet.DigitalSignatureField.createFromField(field);
  if (!(await digSig.hasCryptographicSignature())) return false;
  // PDFNet can miss visible AP on some DigSig widgets; non-zero rect is a fallback.
  if (await digSig.hasVisibleAppearance()) return true;
  return widget.Width > 0 && widget.Height > 0;
}

async function flattenPdfNetSignatureWidgets(
  pdfDoc: PdfDoc,
  fieldNamesToFlatten: Set<string>,
  pageNumbers: number[]
): Promise<FlattenedSignatureWidgets> {
  const flattenedFieldNames = new Set<string>();
  const pagesToRefresh = new Set<number>();

  for (const pageNum of pageNumbers) {
    try {
      const page = await pdfDoc.getPage(pageNum);
      for (let i = (await page.getNumAnnots()) - 1; i >= 0; i--) {
        const annot = await page.getAnnot(i);
        if ((await annot.getType()) !== pdfTronCore.PDFNet.Annot.Type.e_Widget) continue;

        const widgetAnnot = await pdfTronCore.PDFNet.WidgetAnnot.createFromAnnot(annot);
        const field = await widgetAnnot.getField();
        if (!(await field.isValid())) continue;
        const name = await field.getName();
        if (!fieldNamesToFlatten.has(name)) continue;

        await annot.flatten(page);
        flattenedFieldNames.add(name);
        pagesToRefresh.add(pageNum);
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  return { flattenedFieldNames, pagesToRefresh };
}
